import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import { SidebarTrigger } from './ui/sidebar';
import { SemanticLayerTabs, semanticLayerTabLabel, type SemanticLayerTab } from './ViewModeToggle';

export const SEMANTIC_LAYER_BASE_PATH = '/develop/semantic-layer';

interface SemanticLayerPageHeaderProps {
  tab: SemanticLayerTab;
}

/**
 * Shared header for the Semantic Layer tabs (Diagram / Entities / Metrics / Dimensions).
 * Each tab is its own route, so the tab switcher lives in the header and navigates.
 * Page-specific controls belong in a SemanticLayerToolbar below the header, so that the
 * tabs sit in the same place on every tab.
 */
export function SemanticLayerPageHeader({ tab }: SemanticLayerPageHeaderProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 border-b border-border bg-background flex-shrink-0 z-10 gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <SidebarTrigger className="md:hidden h-5 w-5" />
        <div className="flex flex-col gap-1 md:gap-2 min-w-0">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop">Develop</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={SEMANTIC_LAYER_BASE_PATH}>Semantic Layer</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{semanticLayerTabLabel(tab)}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <h1 className="text-lg md:text-xl font-semibold text-foreground">Semantic Layer</h1>
        </div>
      </div>
      <div className="flex items-center">
        <SemanticLayerTabs value={tab} onChange={(next) => navigate(`${SEMANTIC_LAYER_BASE_PATH}/${next}`)} />
      </div>
    </div>
  );
}

/**
 * Row of page-specific controls (search, view toggles, "New ..." buttons) directly below
 * the Semantic Layer header. With two children the first sits left and the second right.
 */
export function SemanticLayerToolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 w-full px-4 md:px-6 py-3 border-b border-border bg-background flex-shrink-0">
      {children}
    </div>
  );
}
